// Shared shell for every auth screen — login, signup, request-reset,
// set-new-password. Two-column layout on desktop: a rich brand panel on
// the left (logo + headline + value props + trust strip) and a form
// panel on the right with whatever auth flow is active.
//
// Brand palette matches the marketing site (sixfigureagentcrm.com):
//   - background: deep black gradient with a gold radial glow + dot grid
//   - accent: gold (Tailwind amber-300 / amber-400 / amber-500)
//   - form CTAs: gold buttons with black text — high-contrast and
//     consistent with the brand identity.
//
// On mobile (< lg) the brand panel hides and the form panel takes the
// whole viewport with a brand mark above the form card.

import React from 'react';
import BrandLogo, { BRAND_NAME, BRAND_TAGLINE } from './BrandLogo';

// Subtle dot-grid texture for the dark panel — no asset, pure CSS.
const DOT_GRID = {
  backgroundImage: 'radial-gradient(rgba(251,191,36,0.10) 1px, transparent 1px)',
  backgroundSize: '22px 22px',
};

export default function AuthLayout({ children, eyebrow, heading, subheading, footer }) {
  return (
    <div className="min-h-screen flex bg-slate-950">
      {/* ── Brand panel (desktop only) ────────────────────────────── */}
      <div className="hidden lg:flex lg:w-[46%] xl:w-1/2 relative overflow-hidden bg-gradient-to-br from-black via-slate-950 to-slate-900 text-white">
        {/* Layered decoration: gold glows, dot grid, bottom fade. */}
        <div className="pointer-events-none absolute inset-0" style={DOT_GRID} aria-hidden="true" />
        <div className="pointer-events-none absolute -top-40 -right-32 w-[28rem] h-[28rem] rounded-full bg-amber-400/20 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute top-1/3 -left-40 w-[26rem] h-[26rem] rounded-full bg-amber-600/10 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/80 to-transparent" aria-hidden="true" />

        <div className="relative z-10 w-full max-h-screen overflow-y-auto">
        <div className="min-h-screen flex flex-col justify-between w-full p-12 xl:p-16">
          {/* Top — logo + badge */}
          <div className="flex items-center justify-between">
            <BrandLogo size="md" />
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-300/80 border border-amber-300/25 rounded-full px-3 py-1.5 bg-amber-400/5">
              Agent Operating System
            </span>
          </div>

          {/* Middle — headline + value props */}
          <div className="max-w-lg my-10">
            <h2 className="text-[2.6rem] xl:text-5xl font-black leading-[1.05] tracking-tight">
              {BRAND_TAGLINE}
            </h2>
            <p className="text-slate-300/90 mt-5 text-base leading-relaxed">
              The agent operating system for your book of business —
              multi-carrier statements, override math, persistency, and
              chargeback prevention in one dashboard.
            </p>

            <ul className="mt-9 space-y-3">
              <BrandFeature title="Track every commission" detail="MOO, Americo, Transamerica — drop the PDF, we parse it." />
              <BrandFeature title="Cut chargebacks before they hit" detail="Lapse-window alerts and persistency tracking." />
              <BrandFeature title="Improve retention" detail="Client follow-ups, payment reminders, win-back queue." />
            </ul>

            {/* AI — how the system uses it */}
            <div className="mt-8 rounded-2xl bg-gradient-to-br from-amber-400/[0.12] via-amber-500/[0.06] to-transparent ring-1 ring-amber-300/25 p-5">
              <div className="flex items-center gap-2">
                <svg viewBox="0 0 24 24" className="w-4 h-4 text-amber-300" fill="currentColor" aria-hidden="true">
                  <path d="M12 2l1.7 5.6L19 9.3l-5.3 1.7L12 16l-1.7-5L5 9.3l5.3-1.7z" />
                  <path d="M18.5 13.5l.9 2.6 2.6.9-2.6.9-.9 2.6-.9-2.6-2.6-.9 2.6-.9z" opacity="0.65" />
                </svg>
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-amber-300">Powered by AI</span>
              </div>
              <p className="text-sm text-slate-300/90 mt-2.5 leading-relaxed">
                AI Employees work your book around the clock — so the busywork runs itself.
              </p>
              <ul className="mt-3 space-y-2">
                <AiPoint text="Prospect outreach & onboarding — drafted and sent automatically" />
                <AiPoint text="Dunning + churn-save catches failed payments and at-risk clients" />
                <AiPoint text="Auto-replies to inbound texts and times every follow-up" />
                <AiPoint text="Reads carrier statements the moment you drop a PDF" />
              </ul>
            </div>
          </div>

          {/* Bottom — trust strip + copyright */}
          <div>
            <div className="flex flex-wrap gap-2.5">
              {['Multi-carrier parsing', 'Override math', 'Real-time alerts'].map((t) => (
                <span key={t} className="text-xs font-medium text-amber-100/80 bg-white/5 ring-1 ring-white/10 rounded-lg px-3 py-1.5">
                  {t}
                </span>
              ))}
            </div>
            <div className="mt-8 text-xs text-slate-500">
              © {new Date().getFullYear()} {BRAND_NAME}
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* ── Form panel ────────────────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-gradient-to-br from-white via-slate-50 to-slate-100 relative">
        {/* faint top gold wash so the white side isn't sterile */}
        <div className="pointer-events-none absolute top-0 inset-x-0 h-24 bg-gradient-to-b from-amber-100/40 to-transparent" aria-hidden="true" />

        <div className="w-full max-w-md relative">
          {/* Mobile brand mark — hidden once the brand panel takes over. */}
          <div className="lg:hidden mb-7 flex justify-center">
            <div className="bg-black rounded-2xl px-5 py-4 shadow-xl ring-1 ring-amber-400/20">
              <BrandLogo size="sm" />
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl shadow-slate-900/10 ring-1 ring-slate-200/70 overflow-hidden">
            {/* gold accent bar across the top of the card */}
            <div className="h-1.5 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-300" aria-hidden="true" />
            <div className="p-8 sm:p-10">
              {eyebrow && (
                <div className="text-xs uppercase tracking-[0.15em] font-bold text-amber-700">
                  {eyebrow}
                </div>
              )}
              {heading && (
                <h1 className="text-3xl font-black tracking-tight text-slate-900 mt-1">
                  {heading}
                </h1>
              )}
              {subheading && (
                <div className="text-sm text-slate-500 mt-2 leading-relaxed">{subheading}</div>
              )}
              <div className={`${heading || subheading ? 'mt-7' : ''}`}>
                {children}
              </div>
              {footer && (
                <div className="mt-7 pt-6 border-t border-slate-100 text-center text-sm text-slate-500">
                  {footer}
                </div>
              )}
            </div>
          </div>

          {/* tiny reassurance line under the card */}
          <p className="text-center text-xs text-slate-400 mt-5">
            Secure, encrypted sign-in · {BRAND_NAME}
          </p>
        </div>
      </div>
    </div>
  );
}

function AiPoint({ text }) {
  return (
    <li className="flex items-start gap-2.5 text-sm text-slate-300">
      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" aria-hidden="true" />
      <span className="leading-snug">{text}</span>
    </li>
  );
}

function BrandFeature({ title, detail }) {
  return (
    <li className="flex items-start gap-3.5">
      <div className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-lg bg-amber-400/15 ring-1 ring-amber-300/40 flex items-center justify-center" aria-hidden="true">
        <svg viewBox="0 0 12 12" className="w-3.5 h-3.5 text-amber-300" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="2.5 6.5 5 9 9.5 3.5" />
        </svg>
      </div>
      <div>
        <div className="font-semibold text-white leading-snug">{title}</div>
        <div className="text-sm text-slate-400 leading-snug">{detail}</div>
      </div>
    </li>
  );
}
